<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import type {PrimaryFeeling} from '@/ts/types/dto/interventions-dto';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import AriaListBox from '@/vue/molecules/aria-list-box.vue';
import type {ListBoxOption} from '@/ts/types/component/aria-list-box.type';
import FeelingChip from '@/vue/molecules/feeling-chip.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';

const props = defineProps<{
  feelings: PrimaryFeeling[];
}>();
const model = defineModel<string[]>('ids');
const isOpen = defineModel<boolean>('isOpen');
const {t: $t} = useI18n();


const options = computed<ListBoxOption[]>(() => {
  return (props.feelings ?? []).map<ListBoxOption>((feeling: PrimaryFeeling) => {
    return {
      id: feeling.id,
      text: feeling.name,
    };
  });
});

const onClickClose = (): void => {
  isOpen.value = false;
};

const headlineId = `feelings-popup-headline-${  crypto.randomUUID()}`;


</script>

<template>
  <balm-dialog v-model="isOpen" type="fit-content" theme="neutral-0">
    <div class="dialog-content">
      <h2 :id="headlineId" class="text-body-bold headline">
        {{ $t('select_what_you') }}
      </h2>

      <aria-list-box v-model="model!" orientation="ambiguous" :labeled-by="headlineId" :options="options">
        <template #item="templateProps">
          <feeling-chip :name="templateProps.option.text" :is-selected="templateProps.isSelected"/>
        </template>
      </aria-list-box>

      <balm-btn
        class="btn"
        label="Done"
        theme="grey"
        paddings="none"
        @click="onClickClose"
      />
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">

.headline {
  margin-top: var(--Spacing-spacing-5);
  margin-bottom: var(--Spacing-spacing-4);
  padding-right: var(--Spacing-spacing-8);

  @include screen-lg {
    margin-top: var(--Spacing-spacing-4);
  }
}

.dialog-content {
  @include screen-lg {
    width: 340px;
  }
}

:deep(.listbox) {
  display: flex;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);
  margin-bottom: var(--Spacing-spacing-6);
}

:deep(.listbox__option) {
  cursor: pointer;
  border-radius: var(--button-border-radius);
}

.btn {
  width: 100%;
}

</style>
